import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isString } from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  startWith,
  switchMap,
} from 'rxjs';

import {
  ColumnLinkInfo,
  ComparisonOperator,
  LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { DatasetsApiService, GetPaginatedRequestModel } from '@blaze-shared/datasets-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import {
  BlazeTableRecord,
  DatasetRowLinkService,
  SchemaService,
} from '@blaze-shared/schema-data-access';
import { DatasetRow, DatasetSchema, findColumn } from '@blaze-shared/schema-domain';

import {
  CellValueUpdateFromLink,
  CellValueUpdateFromTag,
  InlineEditorConfig,
  type InlineEditorLayoutMode,
} from '../base-editor/inline-editor.types';

type BlazeTableRecordValue = BlazeTableRecord['value'];

@UntilDestroy()
@Component({
  selector: 'be-link-menu',
  templateUrl: './link-menu.component.html',
  styleUrls: ['./link-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableLinkMenuComponent implements AfterViewInit {
  private readonly datasetRowLinkService = inject(DatasetRowLinkService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly schemaService = inject(SchemaService);

  valueChange = output<CellValueUpdateFromLink | BlazeTableRecordValue | BlazeTableRecordValue[]>();

  @Input() value:
    | string
    | number
    | string[]
    | null
    | undefined
    | BlazeTableRecordValue
    | BlazeTableRecordValue[];
  @Input() layoutMode?: InlineEditorLayoutMode;

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger | undefined;
  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild('nextPage') nextPage!: ElementRef<HTMLDivElement>;

  private readonly searchText$$ = new BehaviorSubject<string>('');
  private readonly linkConfig$$ = new BehaviorSubject<ColumnLinkInfo | undefined | null>(null);

  private requestBody?: GetPaginatedRequestModel;
  private relatedColumns: string[] = [];
  private nextPageLoading = false;

  private _config?: InlineEditorConfig;
  private valueForInputLike: BlazeTableRecordValue | BlazeTableRecordValue[] | undefined;

  anyRecordsLoaded = signal<boolean>(false);

  nextPageToken?: string | null;

  records = signal<BlazeTableRecord[]>([]);
  filteredRecords$!: Observable<BlazeTableRecord[]>;
  rows!: DatasetRow[];
  searchFormCtrl = new UntypedFormControl();

  readonly getFileUrlHref = urlUtils.getFileUrlHref;

  @Input() set config(value: InlineEditorConfig | undefined) {
    this._config = value;
    this.linkConfig$$.next(this._config?.columnSchema?.link);
  }
  get config(): InlineEditorConfig | undefined {
    return this._config;
  }

  get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }

  get isMultiple(): boolean {
    return !!this.config?.columnSchema?.multipleValues;
  }

  get link(): ColumnLinkInfo | undefined | null {
    return this.config?.columnSchema?.link;
  }

  constructor() {
    combineLatest([this.getRelatedColumns(), this.searchText$$.asObservable().pipe(startWith(''))])
      .pipe(
        map(([{ allRelatedColumns, relatedStringColumns }, searchText]) => {
          this.records.set([]);
          const requestBodyWithoutProjectId: GetPaginatedRequestModel = {
            pagination: {
              maxCount: 100,
            },
            projectColumns: [...new Set(allRelatedColumns || [])],
            filter: !searchText
              ? null
              : ({
                  nodeType: QueryNodeType.Logical,
                  logicalType: LogicalType.Or,
                  children: relatedStringColumns.map((column) => ({
                    children: [],
                    column,
                    displayInfos: [],
                    ignoreIfEmptyValue: false,
                    isFormulaValue: false,
                    isOptional: false,
                    nodeType: QueryNodeType.Comparison,
                    operator: ComparisonOperator.ContainsText,
                    value: searchText,
                  })),
                } satisfies LogicalQueryNode),
          };

          this.requestBody = requestBodyWithoutProjectId;
          this.nextPageToken = undefined;
          this.nextPageLoading = false;

          return requestBodyWithoutProjectId;
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        const tableId = this.link?.tableId;
        if (!tableId || !this.requestBody) {
          return;
        }
        this.loadNextPage();
      });

    // Subscribe to search text changes to trigger data fetching
    this.searchFormCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.searchText$$.next(searchValue ?? '');
      });
  }

  ngAfterViewInit(): void {
    if (this.layoutMode !== 'input-like') {
      this.openPickerMenu();
    }
  }

  preventEventAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  selectRecord(event: Event, link: BlazeTableRecord): void {
    if (this.layoutMode === 'input-like') {
      if (this.isMultiple) {
        if (Array.isArray(this.valueForInputLike)) {
          this.valueForInputLike = [...this.valueForInputLike, link.value];
        } else {
          this.valueForInputLike = [link.value];
        }
        this.value = this.valueForInputLike.map((v) =>
          this.getRecordDisplayValue({ value: v } as BlazeTableRecord),
        );
      } else {
        this.valueForInputLike = link.value;
        this.value = this.getRecordDisplayValue(link);
      }
      this.valueChange.emit(this.valueForInputLike as BlazeTableRecordValue);
    } else {
      this.valueChange.emit({ actionType: 'add', link });
    }
    event.preventDefault();
  }

  removeTag(link: string): void {
    if (this.isMultiple) {
      if (Array.isArray(this.valueForInputLike)) {
        this.valueForInputLike = this.valueForInputLike.filter(
          (v) => this.getLinkDisplayValue({ value: v } as BlazeTableRecord) !== link,
        );
        this.value = this.valueForInputLike.map((v) =>
          this.getRecordDisplayValue({ value: v } as BlazeTableRecord),
        );
      } else {
        this.valueForInputLike = undefined;
        this.value = undefined;
      }
      this.valueChange.emit(this.valueForInputLike as BlazeTableRecordValue[]);
    } else {
      this.value = undefined;
      this.valueChange.emit(this.value);
    }
  }

  scrollBottom($event: any): void {
    const target = $event.target;
    const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 10;
    if (this.nextPageToken && isAtBottom) {
      this.loadNextPage();
    }
  }

  loadNextPage(): void {
    if (!this.link?.tableId || !this.requestBody || this.nextPageLoading) {
      return;
    }

    this.nextPageLoading = true;
    const nextPage = this.nextPageToken;

    const nextPageRequestBody: GetPaginatedRequestModel = {
      ...this.requestBody,
      pagination: {
        ...this.requestBody.pagination,
        ...(nextPage && { pageToken: nextPage }),
      },
    };
    const tableId = this.link.tableId;

    this.datasetsApiService
      .postGetPaginated(tableId, nextPageRequestBody)
      .pipe(
        switchMap((response) => {
          this.nextPageToken = response.pagination.nextPageToken;
          return this.datasetRowLinkService.createRecords(
            this.link,
            response.data,
            this.relatedColumns,
          );
        }),
        untilDestroyed(this),
        finalize(() => (this.nextPageLoading = false)),
      )
      .subscribe((records) => {
        this.records.set(this.records().concat(records));

        this.anyRecordsLoaded.set(true);
        this.menuTrigger?.menu?.resetActiveItem();
        //TODO: Refactor to avoid using 2x setTimeout with focus
        setTimeout(() => {
          const input = this.filterInput.nativeElement;
          const inputLength = input.value.length;

          input.focus();
          input.select();
          input.setSelectionRange(inputLength, inputLength);
        }, 300);
        setTimeout(() => {
          if (this.layoutMode !== 'input-like') {
            this.openPickerMenu();
            const input = this.filterInput.nativeElement;
            const inputLength = input.value.length;

            input.focus();
            input.select();
            input.setSelectionRange(inputLength, inputLength);
          }
        });
      });
  }

  getRecordDisplayValue(record: BlazeTableRecord): string {
    return this.datasetRowLinkService.getRecordDisplayValue(record);
  }

  getRecordLinkedValue(record?: BlazeTableRecordValue | BlazeTableRecordValue[] | string): string {
    return this.datasetRowLinkService.getRecordLinkedValue(record);
  }

  getLinkDisplayValue(tableRecord: BlazeTableRecord): string {
    return this.datasetRowLinkService.getLinkDisplayValue(tableRecord);
  }

  externalClickAction(event: CellValueUpdateFromTag): void {
    if (event.actionType === 'remove') {
      this.removeTag(event.tag);
    } else {
      this.openPickerMenu();
    }
  }

  private getRelatedColumns(): Observable<{
    allRelatedColumns: string[];
    relatedStringColumns: string[];
  }> {
    return this.linkConfig$$.asObservable().pipe(
      filter(Boolean),
      switchMap((link: ColumnLinkInfo) => this.mapSchemaToRelatedColumns(link.tableId)),
    );
  }

  private mapSchemaToRelatedColumns(
    tableId: string,
  ): Observable<{ allRelatedColumns: string[]; relatedStringColumns: string[] }> {
    return this.schemaService.obtainSchema<DatasetSchema>(tableId).pipe(
      map((schema) => {
        schema?.columns.find((c) =>
          c.dependentColumnInfos?.find((depColInfo) =>
            this.config?.schema?.columns.find((col) => col.id === depColInfo.columnId),
          ),
        );
        const relatedColumns =
          schema?.columns
            .filter((c) =>
              c.dependentColumnInfos?.find((depColInfo) =>
                this.config?.schema?.columns.find((col) => col.id === depColInfo.columnId),
              ),
            )
            .map((col) => col.name) || [];

        this.relatedColumns = relatedColumns;
        const allRelatedColumns = [
          schema?.displayColumn,
          ...relatedColumns,
          ...[
            this.link?.columnName,
            this.link?.displayColumn,
            schema?.columns.find((col) => col.isId)?.name,
          ],
        ]
          .filter((col) => isString(col))
          .filter(Boolean);

        const relatedStringColumns = [
          ...new Set(
            allRelatedColumns.filter((col) => {
              const column = findColumn(schema?.columns, { name: col });
              return (
                (column?.dataType === 'String' ||
                  (column?.dataType === 'Link' && column.link?.dataType === 'String')) &&
                !column.isId
              );
            }),
          ),
        ];

        return { allRelatedColumns, relatedStringColumns };
      }),
    );
  }

  private openPickerMenu(): void {
    this.menuTrigger?.openMenu();
  }
}
